"use client";

import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { isBotUserAgent, isNonInteractiveWebClient } from "../device";
import { logger } from "../logging";

export type MonitoringUser = {
  id: string;
  email?: string;
  name?: string;
  [key: string]: string | number | boolean | undefined;
};

const SERVICE_NAME = "ageless-ai-website";
const SITE = "datadoghq.com";
const DEFAULT_SESSION_SAMPLE_RATE = 100;
const DEFAULT_SESSION_REPLAY_SAMPLE_RATE = 100;

const MEDIAPIPE_NOISE =
  /gl_context\.cc|Graph successfully started running|Created TensorFlow Lite XNNPACK delegate|vision_wasm_internal|@mediapipe\/tasks-vision/;

const MEDIAPIPE_WASM_ABORT =
  /Aborted(\(\))?|Build with -sASSERTIONS|both async and sync fetching of the wasm failed/;

const MEDIAPIPE_WASM_WEBGL =
  /activeTexture|_emscripten_gl|bindFramebuffer|bindTexture|getParameter/;

const MEDIAPIPE_RUM_ERROR_MESSAGE =
  /Object Not Found Matching Id:.*MethodName:update, ParamCount:4/;

const RESIZE_OBSERVER_LOOP_NOISE =
  /resizeobserver loop (?:limit exceeded|completed with undelivered notifications)/i;

const TANSTACK_QUERY_ABORT_STACK = /@tanstack\/query-core|removeQueries|Object\.onCancel/;

const FETCH_ABORT_MESSAGE = /signal is aborted(?: without reason)?|fetch is aborted/i;

const ASSESSMENT_DEFINE_FALSY_TOKEN_NOISE = /defineAssessmentByAnonymousId returned falsy token/i;

// Browser extensions (password managers, ad blockers, etc.) inject content scripts that
// call chrome.runtime.sendMessage after the host tab closes. No application frames appear
// in the stack — only this generic message.
const BROWSER_EXTENSION_RUNTIME_NOISE = /invalid call to runtime\.sendmessage\(\)\. tab not found/i;

// Structured face-detection telemetry uses `"event":"face_landmarker_*"` /
// `"event":"facial_thirds_*"` JSON envelopes. Keep those log lines for failure
// rate measurement, but strip attached native MediaPipe WASM/WebGL errors so
// Error Tracking does not reopen on handled GPU delegate failures (meta.message
// already carries the failure text).
const INSTRUMENTED_VISION_EVENT = /"event":"[^"]*(?:face_landmarker|facial_thirds)/;

function isMediapipeSpecificNoise(text: string): boolean {
  return MEDIAPIPE_NOISE.test(text);
}

function isMediapipeWasmAbortNoise(...parts: (string | undefined)[]): boolean {
  const combined = parts.filter((part): part is string => typeof part === "string").join("\n");
  return MEDIAPIPE_WASM_ABORT.test(combined) && MEDIAPIPE_NOISE.test(combined);
}

function isMediapipeWasmWebglNoise(...parts: (string | undefined)[]): boolean {
  const combined = parts.filter((part): part is string => typeof part === "string").join("\n");
  return MEDIAPIPE_WASM_WEBGL.test(combined) && MEDIAPIPE_NOISE.test(combined);
}

function isMediapipeWasmNativeNoise(...parts: (string | undefined)[]): boolean {
  return isMediapipeWasmAbortNoise(...parts) || isMediapipeWasmWebglNoise(...parts);
}

type LogsBeforeSendEvent = {
  origin?: string;
  message?: string;
  error?: { message?: string; stack?: string };
  view?: { url?: string };
};

// Query params carrying bearer credentials that must never reach Datadog. Mirrors the
// analytics-side SENSITIVE_ANALYTICS_QUERY_PARAMS denylist (segment.client):
// "embedToken" is the embed session JWT; "token" is the assessment JWT consumed by
// AssessmentSessionProvider (results share links, texted consult-capture links).
const SENSITIVE_URL_PARAMS = ["embedToken", "token"] as const;

/**
 * Strips credential-bearing query params from a URL. Fails CLOSED: an unparseable
 * value is reduced to its pre-query portion rather than shipped unsanitized.
 */
function stripSensitiveUrlParams(value: string): string {
  try {
    const url = new URL(value);
    for (const param of SENSITIVE_URL_PARAMS) {
      url.searchParams.delete(param);
    }
    return url.toString();
  } catch {
    return value.split("?")[0] ?? "";
  }
}

function isInstrumentedVisionEventMessage(message: string | undefined): boolean {
  return typeof message === "string" && INSTRUMENTED_VISION_EVENT.test(message);
}

function stripMediapipeWasmNativeFromInstrumentedVisionLog(log: LogsBeforeSendEvent): void {
  if (!isInstrumentedVisionEventMessage(log.message)) {
    return;
  }
  const err = log.error;
  const errMessage = typeof err?.message === "string" ? err.message : undefined;
  const errStack = typeof err?.stack === "string" ? err.stack : undefined;
  if (isMediapipeWasmNativeNoise(log.message, errMessage, errStack)) {
    delete log.error;
  }
}

function isMediapipeLogsNoise(log: LogsBeforeSendEvent): boolean {
  if (isInstrumentedVisionEventMessage(log.message)) {
    return false;
  }
  if (typeof log.message === "string" && isMediapipeSpecificNoise(log.message)) {
    return true;
  }
  const err = log.error;
  const errMessage = typeof err?.message === "string" ? err.message : undefined;
  const errStack = typeof err?.stack === "string" ? err.stack : undefined;

  if (errMessage && isMediapipeSpecificNoise(errMessage)) {
    return true;
  }
  if (errStack && isMediapipeSpecificNoise(errStack)) {
    return true;
  }
  return isMediapipeWasmNativeNoise(log.message, errMessage, errStack);
}

type RumErrorEvent = {
  type: string;
  error?: { message?: string; stack?: string; source?: string; type?: string } | null;
};

function isResizeObserverLoopNoise(...parts: (string | undefined)[]): boolean {
  const combined = parts.filter((part): part is string => typeof part === "string").join("\n");
  return RESIZE_OBSERVER_LOOP_NOISE.test(combined);
}

function isBrowserExtensionRuntimeNoise(...parts: (string | undefined)[]): boolean {
  const combined = parts.filter((part): part is string => typeof part === "string").join("\n");
  return BROWSER_EXTENSION_RUNTIME_NOISE.test(combined);
}

// Transitional stale-bundle suppression: current bundles never throw this error
// for non-interactive clients (the session provider downgrades it to a warn),
// and known crawlers never even initialize DataDog (see the isBotUserAgent()
// gate in initMonitoring). This filter only catches cached pre-fix bundles that
// still throw it from automation UAs; real-browser occurrences must stay loud.
function isAssessmentDefineFalsyTokenNoise(...parts: (string | undefined)[]): boolean {
  const combined = parts.filter((part): part is string => typeof part === "string").join("\n");
  return ASSESSMENT_DEFINE_FALSY_TOKEN_NOISE.test(combined) && isNonInteractiveWebClient();
}

type AbortCarrier = {
  name?: string;
  kind?: string;
  // RUM error events carry the error class in `type` (Logs events use `kind`).
  type?: string;
  message?: string;
  stack?: string;
};

function isTanstackQueryAbortNoise(error?: AbortCarrier | null, message?: string): boolean {
  const errMessage = typeof error?.message === "string" ? error.message : "";
  const errStack = typeof error?.stack === "string" ? error.stack : "";
  const logMessage = typeof message === "string" ? message : "";

  const isAbort =
    error?.name === "AbortError" ||
    error?.kind === "AbortError" ||
    error?.type === "AbortError" ||
    errMessage.includes("AbortError") ||
    errStack.startsWith("AbortError:") ||
    logMessage.includes("AbortError");

  if (!isAbort) {
    return false;
  }

  const combined = [errMessage, errStack, logMessage].join("\n");

  if (!FETCH_ABORT_MESSAGE.test(combined) && !combined.includes("AbortError:")) {
    return false;
  }

  return (
    TANSTACK_QUERY_ABORT_STACK.test(combined) ||
    (logMessage.startsWith("Fetch error") && FETCH_ABORT_MESSAGE.test(errMessage))
  );
}

function isMediapipeRumErrorNoise(event: RumErrorEvent): boolean {
  if (event.type !== "error" || !event.error) {
    return false;
  }
  const msg = String(event.error.message ?? "");
  const stack = String(event.error.stack ?? "");

  if (INSTRUMENTED_VISION_EVENT.test(msg)) {
    return isMediapipeWasmNativeNoise(msg, stack);
  }
  if (MEDIAPIPE_RUM_ERROR_MESSAGE.test(msg)) {
    return true;
  }
  if (isMediapipeSpecificNoise(msg) || isMediapipeSpecificNoise(stack)) {
    return true;
  }
  return isMediapipeWasmNativeNoise(msg, stack);
}

let initialized = false;

type MonitoringConfig = {
  applicationId: string;
  clientToken: string;
  env: string;
  version?: string;
  snap?: string;
};

function getMonitoringConfig(): MonitoringConfig | null {
  const env = process.env.NEXT_PUBLIC_DATADOG_ENV;
  const applicationId = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID;
  const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;
  const version = process.env.NEXT_PUBLIC_DATADOG_VERSION;
  const snap = process.env.NEXT_PUBLIC_SNAP_ID;

  if (!env || !applicationId || !clientToken || !version) {
    logger.error("datadog_not_initialized", undefined, new Error("missing_env_vars"));
  }

  if (!snap && process.env.VERCEL_ENV != "production") {
    logger.warn("datadog_snap_missing");
  }

  return {
    env: env ?? "",
    applicationId: applicationId ?? "",
    clientToken: clientToken ?? "",
    version: version ?? "",
    snap: snap ?? "",
  };
}

export function initializeDataDogRum(): void {
  const config = getMonitoringConfig();
  if (!config) return;

  try {
    datadogRum.init({
      applicationId: config.applicationId,
      clientToken: config.clientToken,
      site: SITE,
      service: SERVICE_NAME,
      env: config.env,
      ...(config.version && { version: config.version }),
      sessionSampleRate: DEFAULT_SESSION_SAMPLE_RATE,
      sessionReplaySampleRate: DEFAULT_SESSION_REPLAY_SAMPLE_RATE,
      // Monitoring is decoupled from the cookie banner: collection starts at init
      // for every visitor, restoring the pre-2026-08-05 behaviour. A gate that
      // waits on the banner is not fail-safe here — when the banner is hidden the
      // consent listener never fires at all, so collection stays off permanently
      // and silently (see setMonitoringTrackingConsent, which records the answer
      // as a facet rather than gating on it).
      trackingConsent: "granted",
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input",
      // Session identity is persisted so a session survives a full document load
      // and an anonymous id stitches visits together. Both are deliberate: this is
      // device storage written ahead of consent, accepted as a product decision
      // because the audience is ~98% US/CA (opt-out jurisdictions). Do not "fix"
      // either line back to memory/false without that decision being revisited.
      // sessionPersistence must stay identical to the datadogLogs.init value —
      // the two SDKs share one session and desync if they disagree.
      sessionPersistence: "local-storage",
      trackAnonymousUser: true,
      allowedTracingUrls: [window.location.origin],
      beforeSend: (event) => {
        const rumEvent = event as RumErrorEvent;
        if (isMediapipeRumErrorNoise(rumEvent)) {
          return false;
        }
        if (isResizeObserverLoopNoise(rumEvent.error?.message, rumEvent.error?.stack)) {
          return false;
        }
        if (isTanstackQueryAbortNoise(rumEvent.error)) {
          return false;
        }
        if (isAssessmentDefineFalsyTokenNoise(rumEvent.error?.message, rumEvent.error?.stack)) {
          return false;
        }
        if (isBrowserExtensionRuntimeNoise(rumEvent.error?.message, rumEvent.error?.stack)) {
          return false;
        }
        // RUM records the LANDING url before any client code (including the session
        // provider's history.replaceState token strip) runs, so bearer tokens riding in
        // the query string must be scrubbed here or they reach Datadog verbatim. `view` is
        // absent on non-view events (action/resource/error), so guard before touching it.
        if (typeof event.view?.url === "string") {
          event.view.url = stripSensitiveUrlParams(event.view.url);
        }
        return true;
      },
    });

    datadogRum.setGlobalContextProperty("origin", window.location.origin);

    if (config.snap) {
      datadogRum.setGlobalContextProperty("snap", config.snap);
    }
  } catch (error) {
    logger.error("datadog_rum_init_failed", undefined, error as Error);
    return;
  }
}

export function initializeDataDogLogs(): void {
  const config = getMonitoringConfig();
  if (!config) return;

  try {
    datadogLogs.init({
      clientToken: config.clientToken,
      site: SITE,
      service: SERVICE_NAME,
      env: config.env,
      ...(config.version && { version: config.version }),
      forwardErrorsToLogs: true,
      forwardConsoleLogs: ["warn", "error"],
      sessionSampleRate: 100,
      // Same as the RUM init above; the two SDKs must start in the same
      // collection state.
      trackingConsent: "granted",
      // Must match the datadogRum.init values — the two SDKs share one session.
      // Historically Logs omitted sessionPersistence and silently fell back to the
      // "cookie" default while RUM used local-storage; that mismatch let the two
      // disagree about session identity, so it is set explicitly here.
      sessionPersistence: "local-storage",
      trackAnonymousUser: true,
      beforeSend: (log) => {
        const logsEvent = log as LogsBeforeSendEvent;
        stripMediapipeWasmNativeFromInstrumentedVisionLog(logsEvent);
        if (isMediapipeLogsNoise(logsEvent)) {
          return false;
        }
        if (
          isResizeObserverLoopNoise(
            logsEvent.message,
            logsEvent.error?.message,
            logsEvent.error?.stack,
          )
        ) {
          return false;
        }
        if (isTanstackQueryAbortNoise(logsEvent.error, logsEvent.message)) {
          return false;
        }
        if (
          isAssessmentDefineFalsyTokenNoise(
            logsEvent.message,
            logsEvent.error?.message,
            logsEvent.error?.stack,
          )
        ) {
          return false;
        }
        if (
          isBrowserExtensionRuntimeNoise(
            logsEvent.message,
            logsEvent.error?.message,
            logsEvent.error?.stack,
          )
        ) {
          return false;
        }
        if (typeof logsEvent.view?.url === "string") {
          logsEvent.view.url = stripSensitiveUrlParams(logsEvent.view.url);
        }
        return true;
      },
    });

    datadogLogs.setGlobalContextProperty("origin", window.location.origin);

    if (config.snap) {
      datadogLogs.setGlobalContextProperty("snap", config.snap);
    }
  } catch (error) {
    logger.error("datadog_logs_init_failed", undefined, error as Error);
    return;
  }
}

export function initMonitoring(): void {
  if (initialized || typeof window === "undefined") return;

  // Crawlers (Googlebot alone was ~60% of this service's error volume) render
  // pages but never complete flows — their RUM/log telemetry is pure noise and
  // ingestion cost. Real users in in-app browsers are not matched.
  if (isBotUserAgent()) return;

  initializeDataDogRum();
  initializeDataDogLogs();

  initialized = true;
}

/**
 * Bridge from the consent layer (the instrumentation stack's datadog sink).
 * Datadog collects regardless of the analytics scope, so this neither starts
 * nor stops collection — it records the visitor's banner answer as an
 * `analytics_consent` facet on RUM and Logs. That keeps opt-in rate measurable and lets analyses
 * that require consent filter down to the consented population.
 */
// TODO(naming): misnomer — sets a facet, not tracking consent; rename on a future touch.
export function setMonitoringTrackingConsent(granted: boolean): void {
  initMonitoring();
  if (!initialized) return;

  datadogRum.setGlobalContextProperty("analytics_consent", granted);
  datadogLogs.setGlobalContextProperty("analytics_consent", granted);
}

export function setMonitoringUser(user: MonitoringUser): void {
  initMonitoring();
  if (!initialized) return;
  datadogRum.setUser(user);
}

type IdentifyPayloadTraits = {
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
};

/**
 * Derive and set Datadog RUM user from an analytics identify payload.
 * Centralises the fullName assembly so every call-site that builds
 * an AnalyticsIdentifyPayload can reuse it.
 */
export function setMonitoringUserFromIdentifyPayload(payload: {
  userId: string;
  traits: IdentifyPayloadTraits;
}): void {
  const fullName = [payload.traits.firstName, payload.traits.lastName].filter(Boolean).join(" ");

  setMonitoringUser({
    id: payload.userId,
    email: payload.traits.email ?? undefined,
    name: fullName || undefined,
  });
}

export function clearMonitoringUser(): void {
  initMonitoring();
  if (!initialized) return;
  datadogRum.clearUser();
}

export function addMonitoringError(error: Error, context?: Record<string, unknown>): void {
  initMonitoring();
  if (!initialized) return;
  datadogRum.addError(error, context);
}

export function addMonitoringAction(name: string, context?: Record<string, unknown>): void {
  initMonitoring();
  if (!initialized) return;
  datadogRum.addAction(name, context);
}
